import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash, X, CheckCircle } from "@phosphor-icons/react";

const emptyForm = () => ({
    board: "kpsc", exam_id: "", subject: "", topic: "", prompt: "",
    marks: 15, difficulty: "medium", source_year: null,
});

export default function AdminMainsPrompts() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [board, setBoard] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/mains-prompts", { params: { q, board } });
            setItems(data.items);
        } catch (e) { toast.error("Failed to load prompts"); }
        finally { setLoading(false); }
    }, [q, board]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form.prompt.trim()) { toast.error("Prompt is required"); return; }
        try {
            if (editing) { await api.put(`/admin/mains-prompts/${editing}`, form); toast.success("Prompt updated"); }
            else { await api.post("/admin/mains-prompts", form); toast.success("Prompt created"); }
            setEditing(null); setForm(emptyForm()); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this prompt?")) return;
        try { await api.delete(`/admin/mains-prompts/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error("Delete failed"); }
    };

    const openEdit = (p) => {
        setEditing(p.prompt_id);
        setForm({ board: p.board || "kpsc", exam_id: p.exam_id || "", subject: p.subject || "", topic: p.topic || "", prompt: p.prompt || "", marks: p.marks || 15, difficulty: p.difficulty || "medium", source_year: p.source_year || null });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input data-testid="m-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prompt text…" className="w-full pl-9 pr-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm focus:outline-none focus:border-[#1E3A8A]" />
                </div>
                <select value={board} onChange={(e) => setBoard(e.target.value)} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                    <option value="">All boards</option>
                    <option value="kpsc">KPSC</option><option value="ksp">KSP</option><option value="kea">KEA</option>
                </select>
                <button data-testid="m-new" onClick={() => { setEditing(null); setForm(emptyForm()); }} className="ml-auto btn-primary !py-2 !px-4"><Plus size={16} weight="bold" /> New Prompt</button>
            </div>

            {/* Editor */}
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold">{editing ? "Edit prompt" : "New prompt"}</h3>
                    {editing && <button onClick={() => { setEditing(null); setForm(emptyForm()); }} className="text-xs text-slate-500 flex items-center gap-1"><X size={12} /> Cancel</button>}
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                    <select value={form.board} onChange={(e) => setForm({ ...form, board: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                        <option value="kpsc">KPSC</option><option value="ksp">KSP</option><option value="kea">KEA</option>
                    </select>
                    <input placeholder="Exam ID" value={form.exam_id} onChange={(e) => setForm({ ...form, exam_id: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                </div>
                <textarea data-testid="m-prompt" placeholder="Question / prompt text" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} rows={4} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" />
                <div className="flex items-center gap-3 text-xs">
                    <label>Marks <input type="number" value={form.marks} onChange={(e) => setForm({ ...form, marks: parseInt(e.target.value || "0") })} className="w-20 ml-1 px-2 py-1 border-2 border-[#BFD3FF] rounded-md" /></label>
                    <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="px-2 py-1 border-2 border-[#BFD3FF] rounded-md">
                        <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                    </select>
                    <label>Source year <input type="number" value={form.source_year || ""} onChange={(e) => setForm({ ...form, source_year: e.target.value ? parseInt(e.target.value) : null })} className="w-24 ml-1 px-2 py-1 border-2 border-[#BFD3FF] rounded-md" /></label>
                    <button data-testid="m-save" onClick={save} className="ml-auto btn-primary !py-2 !px-4"><CheckCircle size={16} weight="fill" /> {editing ? "Update" : "Create"}</button>
                </div>
            </div>

            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b-2 border-[#BFD3FF] text-xs uppercase tracking-widest font-bold text-slate-600">{loading ? "Loading…" : `${items.length} prompt(s)`}</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
                            <tr><th className="px-4 py-2 font-bold">Board</th><th className="px-4 py-2 font-bold">Subject/Topic</th><th className="px-4 py-2 font-bold">Prompt</th><th className="px-4 py-2 font-bold">Marks</th><th className="px-4 py-2 font-bold text-right w-24">Actions</th></tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.prompt_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-3 uppercase text-xs font-bold text-[#1E3A8A]">{p.board}</td>
                                    <td className="px-4 py-3 text-xs">{[p.subject, p.topic].filter(Boolean).join(" · ")}</td>
                                    <td className="px-4 py-3 text-xs text-slate-700 max-w-md truncate">{p.prompt}</td>
                                    <td className="px-4 py-3 text-xs tabular-nums">{p.marks}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => openEdit(p)} className="p-1.5 text-slate-500 hover:text-[#1E3A8A]"><PencilSimple size={14} /></button>
                                        <button onClick={() => remove(p.prompt_id)} className="p-1.5 text-slate-500 hover:text-red-600"><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">No prompts.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
